"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Link from "next/link";

export default function LoginPage() {
  const router = useRouter();
  const [tenantSlug, setTenantSlug] = useState(
    process.env.NODE_ENV === "development" ? "hypnosepraktijktwente" : "",
  );
  const [email, setEmail] = useState(
    process.env.NODE_ENV === "development" ? "owner@mijnpatient.nl" : "",
  );
  const [password, setPassword] = useState(
    process.env.NODE_ENV === "development" ? "SterkWachtwoord123!" : "",
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSubdomain, setIsSubdomain] = useState(false);

  // 2FA state
  const [requiresTwoFactor, setRequiresTwoFactor] = useState(false);
  const [pendingToken, setPendingToken] = useState("");
  const [totpCode, setTotpCode] = useState("");

  useEffect(() => {
    const baseDomain = process.env.NEXT_PUBLIC_BASE_DOMAIN ?? "mijnpatient.nl";
    const hostname = window.location.hostname;
    const hasSub =
      hostname !== baseDomain &&
      hostname !== `www.${baseDomain}` &&
      hostname.endsWith(`.${baseDomain}`);
    setIsSubdomain(hasSub);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // On subdomain, tenant is determined by hostname — no need to send tenantSlug
      const payload = isSubdomain
        ? { email, password }
        : { tenantSlug, email, password };

      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.error || "Inloggen mislukt");
        return;
      }

      if (data.requiresTwoFactor) {
        setPendingToken(data.pendingToken);
        setRequiresTwoFactor(true);
        return;
      }

      toast.success("Succesvol ingelogd");
      router.push("/panel");
      router.refresh();
    } catch (error) {
      toast.error("Er is een fout opgetreden");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTotpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch("/api/auth/2fa/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pendingToken, token: totpCode }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.error || "Ongeldige code, probeer opnieuw.");
        setTotpCode("");
        return;
      }

      toast.success("Succesvol ingelogd");
      router.push("/panel");
      router.refresh();
    } catch (error) {
      toast.error("Er is een fout opgetreden");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left Column - Form */}
      <div className="flex items-center justify-center bg-white p-8">
        <div className="w-full max-w-md space-y-8">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary" />
            <span className="text-xl font-semibold">MijnPatient</span>
          </div>

          {/* Header */}
          <div className="space-y-2">
            <h1 className="text-3xl font-bold tracking-tight">
              {requiresTwoFactor ? "Tweestapsverificatie" : "Welkom terug"}
            </h1>
            <p className="text-neutral-600">
              {requiresTwoFactor
                ? "Voer de code uit uw authenticator-app in"
                : "Log in om toegang te krijgen tot je dashboard"}
            </p>
          </div>

          {/* 2FA Form */}
          {requiresTwoFactor ? (
            <form onSubmit={handleTotpSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="totpCode">Voer uw authenticatie-code in</Label>
                <Input
                  id="totpCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="000000"
                  value={totpCode}
                  onChange={(e) =>
                    setTotpCode(e.target.value.replace(/\D/g, ""))
                  }
                  required
                  disabled={isLoading}
                  autoFocus
                  className="text-center text-xl tracking-widest"
                />
                <p className="text-sm text-neutral-500">
                  Code van uw authenticator-app (bijv. Google Authenticator)
                </p>
              </div>
              <Button
                type="submit"
                className="w-full"
                size="lg"
                disabled={isLoading || totpCode.length !== 6}
              >
                {isLoading ? "Verifiëren..." : "Verifiëer"}
              </Button>
              <button
                type="button"
                className="w-full text-sm text-neutral-500 hover:text-neutral-700 underline"
                onClick={() => {
                  setRequiresTwoFactor(false);
                  setPendingToken("");
                  setTotpCode("");
                }}
              >
                Terug naar inloggen
              </button>
            </form>
          ) : (
            <>
              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Bedrijfsnaam field only when not on a tenant subdomain */}
                {!isSubdomain && (
                  <div className="space-y-2">
                    <Label htmlFor="tenantSlug">Bedrijfsnaam</Label>
                    <Input
                      id="tenantSlug"
                      type="text"
                      placeholder="hypnosepraktijktwente"
                      value={tenantSlug}
                      onChange={(e) => setTenantSlug(e.target.value)}
                      required
                      disabled={isLoading}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="jouw@email.nl"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Wachtwoord</Label>
                    <Link
                      href="/forgot-password"
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      Vergeten?
                    </Link>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={isLoading}
                >
                  {isLoading ? "Inloggen..." : "Inloggen"}
                </Button>
              </form>

              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-neutral-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-4 text-neutral-500">
                    Of log in met
                  </span>
                </div>
              </div>

              {/* OAuth Buttons */}
              <div className="grid grid-cols-2 gap-4">
                <Button variant="outline" type="button" disabled>
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      fill="#4285F4"
                    />
                    <path
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      fill="#34A853"
                    />
                    <path
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      fill="#EA4335"
                    />
                  </svg>
                  Google
                </Button>
                <Button variant="outline" type="button" disabled>
                  <svg
                    className="mr-2 h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
                  </svg>
                  Apple
                </Button>
              </div>

              {/* Register Link */}
              <div className="text-center text-sm text-neutral-600">
                Nog geen account?{" "}
                <Link
                  href="/register"
                  className="font-medium text-primary hover:underline"
                >
                  Gratis 2 weken proberen
                </Link>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right Column - Hero */}
      <div className="hidden bg-primary p-12 text-white lg:flex lg:flex-col lg:justify-center">
        <div className="space-y-8">
          <div className="space-y-4">
            <h2 className="text-4xl font-bold leading-tight">
              Beheer je praktijk effectief en overzichtelijk
            </h2>
            <p className="text-lg text-primary-foreground/80">
              Alles wat je nodig hebt om je praktijk soepel te laten draaien.
            </p>
          </div>

          {/* Feature Cards */}
          <div className="space-y-4">
            <Card className="border-none bg-white/10 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="rounded-lg bg-white/20 p-3">
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold">Agenda Beheer</h3>
                    <p className="mt-1 text-sm text-primary-foreground/80">
                      Plan en beheer consulten met gemak
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none bg-white/10 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="rounded-lg bg-white/20 p-3">
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold">Klantenbeheer</h3>
                    <p className="mt-1 text-sm text-primary-foreground/80">
                      Houd alle klantgegevens bij op één plek
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none bg-white/10 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="rounded-lg bg-white/20 p-3">
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold">Facturatie</h3>
                    <p className="mt-1 text-sm text-primary-foreground/80">
                      Verstuur professionele facturen in enkele clicks
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 rounded-lg border border-white/20 bg-white/10 p-6 backdrop-blur-sm">
            <div>
              <p className="text-2xl font-bold">1000+</p>
              <p className="text-sm text-primary-foreground/60">Praktijken</p>
            </div>
            <div>
              <p className="text-2xl font-bold">50K+</p>
              <p className="text-sm text-primary-foreground/60">Consulten</p>
            </div>
            <div>
              <p className="text-2xl font-bold">99.9%</p>
              <p className="text-sm text-primary-foreground/60">Uptime</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 flex items-center justify-between text-sm text-primary-foreground/60">
          <p>Copyright © 2025 MijnPatient</p>
          <Link href="/privacy" className="hover:text-white">
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
}
